import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Copy } from 'lucide-react';
import { MarketingShell } from '@/components/marketing-shell';
import { capture } from '@/lib/posthog';
import { setPageSeo } from '@/lib/brand';
import {
  DOCUMENT_TYPES_BY_SLUG,
  documentTypeSampleJson,
  documentTypeSeoFor,
  type DocumentTypeMeta,
} from './data';

import { Button } from '@/components/ui';

function applySeo(dt: DocumentTypeMeta): void {
  const seo = documentTypeSeoFor(dt.slug);
  if (!seo) return;

  setPageSeo({ title: seo.title, description: seo.description, canonical: seo.canonical });
}

function currency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function CodeTabs({ snippet }: { snippet: { curl: string; node: string } }) {
  const [tab, setTab] = useState<'curl' | 'node'>('curl');
  const [copied, setCopied] = useState(false);
  const code = snippet[tab];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      capture('doctype_invoice_snippet_copied', { tab });
    } catch {
      // Clipboard access can be unavailable in restricted browsers.
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--paper-raised)]">
      <div className="flex items-center justify-between border-b border-[var(--line)] bg-[var(--paper-sunk)] px-4 py-2">
        <div className="flex gap-1">
          {(['curl', 'node'] as const).map((value) => (
            <Button
              variant="unstyled"
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={
                tab === value
                  ? 'rounded-md bg-[var(--paper-raised)] px-3 py-1.5 text-[12px] font-semibold text-[var(--ink)]'
                  : 'rounded-md px-3 py-1.5 text-[12px] font-medium text-[var(--ink-mute)] transition hover:text-[var(--ink)]'
              }
            >
              {value === 'curl' ? 'curl' : 'Node'}
            </Button>
          ))}
        </div>
        <Button
          variant="unstyled"
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--line)] bg-[var(--paper-raised)] px-2.5 py-1 text-[11px] font-medium text-[var(--ink-mute)] transition hover:text-[var(--ink)]"
        >
          {copied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="okra-dev-code overflow-x-auto px-4 py-3 font-mono text-[12.5px] leading-6 text-[var(--ink)]">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function InvoicePreview({ dt }: { dt: DocumentTypeMeta }) {
  return (
    <div className="rounded-lg border border-[var(--line)] bg-[var(--paper-raised)] p-5 shadow-[0_18px_45px_-34px_var(--marketing-heavy-shadow)]">
      <div className="flex items-start justify-between gap-4 border-b border-[var(--line)] pb-4">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase text-[var(--ink-mute)]">
            Sample {dt.docType.toLowerCase()}
          </p>
          <h2 className="mt-1 text-lg font-semibold text-[var(--ink)]">
            {dt.sample.invoiceNumber}
          </h2>
        </div>
        <div className="text-right text-sm text-[var(--ink-mute)]">
          <div>{dt.sample.invoiceDate}</div>
          <div>Due {dt.sample.dueDate}</div>
        </div>
      </div>

      <div className="grid gap-4 border-b border-[var(--line)] py-4 text-sm sm:grid-cols-2">
        <div>
          <p className="font-mono text-[10px] uppercase text-[var(--ink-mute)]">From</p>
          <p className="mt-1 font-semibold text-[var(--ink)]">{dt.sample.vendorName}</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase text-[var(--ink-mute)]">Bill to</p>
          <p className="mt-1 font-semibold text-[var(--ink)]">{dt.sample.customerName}</p>
        </div>
      </div>

      <div className="overflow-x-auto py-4">
        <table className="w-full min-w-[520px] text-left text-xs">
          <thead className="text-[var(--ink-mute)]">
            <tr className="border-b border-[var(--line)]">
              <th className="py-2 pr-3 font-mono font-semibold">SKU</th>
              <th className="px-3 py-2 font-mono font-semibold">Description</th>
              <th className="px-3 py-2 text-right font-mono font-semibold">Qty</th>
              <th className="px-3 py-2 text-right font-mono font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--line-soft)] text-[var(--ink)]">
            {dt.sample.lineItems.map((item) => (
              <tr key={item.sku}>
                <td className="py-2 pr-3 font-mono">{item.sku}</td>
                <td className="px-3 py-2">{item.description}</td>
                <td className="px-3 py-2 text-right">{item.quantity}</td>
                <td className="px-3 py-2 text-right">{currency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="ml-auto grid max-w-xs gap-2 border-t border-[var(--line)] pt-4 text-sm">
        <div className="flex justify-between gap-6 text-[var(--ink-mute)]">
          <dt>Subtotal</dt>
          <dd>{currency(dt.sample.subtotal)}</dd>
        </div>
        <div className="flex justify-between gap-6 text-[var(--ink-mute)]">
          <dt>Tax</dt>
          <dd>{currency(dt.sample.tax)}</dd>
        </div>
        <div className="flex justify-between gap-6 text-base font-semibold text-[var(--ink)]">
          <dt>Total</dt>
          <dd>{currency(dt.sample.total)}</dd>
        </div>
      </dl>
    </div>
  );
}

export function DocumentTypePage() {
  const { slug = '' } = useParams<{ slug?: string }>();
  const dt = DOCUMENT_TYPES_BY_SLUG[slug];

  useEffect(() => {
    if (!dt) return;
    applySeo(dt);
    capture('doctype_page_viewed', {
      slug: dt.slug,
      doc_type: dt.docType,
    });
  }, [dt]);

  if (!dt) return <Navigate to="/document-types" replace />;

  const sampleJson = JSON.stringify(documentTypeSampleJson(dt), null, 2);

  return (
    <MarketingShell>
      <main>
        <section className="border-b border-[var(--line)] bg-[var(--paper-sunk)]">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-16 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(420px,1fr)] lg:px-8 lg:pt-20">
            <div>
              <p className="font-mono text-[11px] font-semibold uppercase text-[var(--ember-text)]">
                {dt.eyebrow}
              </p>
              <h1 className="mt-5 max-w-3xl text-4xl leading-tight text-[var(--ink)] sm:text-5xl lg:text-6xl">
                {dt.h1}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-[var(--ink-mute)]">
                {dt.subhead}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={`/tools/pdf-to-json?template=${dt.slug}`}
                  onClick={() =>
                    capture('doctype_primary_clicked', {
                      destination: '/tools/pdf-to-json',
                      slug: dt.slug,
                    })
                  }
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[var(--ember)] px-5 text-sm font-semibold text-white transition hover:-translate-y-px hover:opacity-90"
                >
                  Try with a PDF
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/pdf-to-link"
                  onClick={() => {
                    capture('doctype_invoice_secondary_clicked', {
                      destination: '/tools/pdf-to-link',
                    });
                  }}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[var(--line)] bg-[var(--paper-raised)] px-5 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--selected-line)]"
                >
                  Start for $0
                </Link>
              </div>
            </div>
            <InvoicePreview dt={dt} />
          </div>
        </section>

        <section className="border-b border-[var(--line)] bg-[var(--paper)] py-14">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)] lg:px-8">
            <div>
              <p className="font-mono text-[11px] font-semibold uppercase text-[var(--ember-text)]">
                Extracted schema
              </p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight text-[var(--ink)]">
                The fields AP teams expect.
              </h2>
              <p className="mt-4 text-base leading-7 text-[var(--ink-mute)]">
                Invoice extraction returns typed values for vendor matching, due-date tracking,
                totals validation, and line-item reconciliation.
              </p>
            </div>
            <ul className="divide-y divide-[var(--line-soft)] rounded-lg border border-[var(--line)] bg-[var(--paper-raised)]">
              {dt.schema.map((field) => (
                <li key={field.name} className="grid gap-1 px-4 py-3 sm:grid-cols-[180px_1fr]">
                  <div className="font-mono text-[12px] font-semibold text-[var(--ink)]">
                    {field.name}
                  </div>
                  <div className="text-sm leading-6 text-[var(--ink-mute)]">
                    {field.description}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-[var(--paper)] py-14">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)] lg:px-8">
            <div>
              <p className="font-mono text-[11px] font-semibold uppercase text-[var(--ember-text)]">
                Sample JSON
              </p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight text-[var(--ink)]">
                One invoice in. One row out.
              </h2>
              <p className="mt-4 text-base leading-7 text-[var(--ink-mute)]">
                The sample invoice preview becomes a JSON object your workflow can validate,
                post to a table, or forward into accounting software.
              </p>
            </div>
            <pre className="okra-dev-code max-h-[520px] overflow-auto rounded-lg border border-[var(--line)] bg-[var(--paper-raised)] p-4 font-mono text-[12px] leading-6 text-[var(--ink)]">
              <code>{sampleJson}</code>
            </pre>
          </div>
        </section>

        <section className="border-y border-[var(--line)] bg-[var(--paper-sunk)] py-14">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.6fr)] lg:px-8">
            <div>
              <p className="font-mono text-[11px] font-semibold uppercase text-[var(--ember-text)]">
                API call
              </p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight text-[var(--ink)]">
                Extract invoices from your backend.
              </h2>
              <p className="mt-4 text-base leading-7 text-[var(--ink-mute)]">
                Use the named invoice workflow when your pipeline knows the document type
                up front and wants stable invoice rows back.
              </p>
            </div>
            <CodeTabs snippet={dt.apiSnippet} />
          </div>
        </section>

        <section className="bg-[var(--paper)] py-14">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="font-mono text-[11px] font-semibold uppercase text-[var(--ink-mute)]">
              Next steps
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[var(--ink)]">
              Pair invoice extraction with the rest of your workflow
            </h2>
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              {dt.relatedLinks.map((item) => {
                const cardClassName =
                  'group block rounded-lg border border-[var(--line)] bg-[var(--paper-raised)] p-5 transition hover:border-[var(--selected-line)]';
                const card = (
                  <>
                    <div className="text-[14px] font-semibold text-[var(--ink)] transition group-hover:text-[var(--ember-text)]">
                      {item.title}
                    </div>
                    <p className="mt-2 text-[13px] leading-6 text-[var(--ink-mute)]">
                      {item.body}
                    </p>
                  </>
                );
                // /blog* is served by the okrapdf-blog Worker, not this SPA —
                // client-side routing dead-ends on it, so those need a full load.
                const outsideSpa = item.href.startsWith('/blog') || /^https?:/i.test(item.href);
                return outsideSpa ? (
                  <a key={item.href} href={item.href} className={cardClassName}>
                    {card}
                  </a>
                ) : (
                  <Link key={item.href} to={item.href} className={cardClassName}>
                    {card}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-t border-[var(--line)] bg-[var(--paper)] py-14">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-semibold text-[var(--ink)]">FAQ</h2>
            <div className="mt-6 divide-y divide-[var(--line)] rounded-lg border border-[var(--line)] bg-[var(--paper-raised)]">
              {dt.faqs.map((faq) => (
                <div key={faq.q} className="p-5">
                  <h3 className="text-base font-semibold text-[var(--ink)]">{faq.q}</h3>
                  <p className="mt-2 text-sm leading-6 text-[var(--ink-mute)]">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </MarketingShell>
  );
}
